/**
 * DateInputExamples.tsx
 * Comprehensive examples of HeroUI DateInput component
 * 
 * Showcases all HeroUI DateInput features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { DateInput } from '@enfomentis/ui';
import { Card, CardHeader, CardBody } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { 
  Calendar,
  Clock,
  CalendarDays,
  Globe,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { today, getLocalTimeZone, parseDate } from '@internationalized/date';
import type { DateValue } from '@internationalized/date';

export const DateInputExamples: React.FC = () => {
  // Individual date input states
  const [dateStates, setDateStates] = useState<Record<string, DateValue | null>>({
    basic: null,
    controlled: today(getLocalTimeZone()),
    birthday: null,
    appointment: null,
    deadline: null,
    meeting: null
  });

  const updateDateState = (key: string) => (value: DateValue | null) => {
    setDateStates(prev => ({ ...prev, [key]: value }));
  };

  const getDateState = (key: string): DateValue | null => {
    return dateStates[key] ?? null;
  };

  const sections = [
    {
      title: "Basic Usage",
      description: "Simple date inputs with different configurations",
      examples: [
        {
          title: "Default Date Input",
          component: (
            <div className="space-y-4 w-full max-w-xs">
              <DateInput 
                label="Select Date"
                value={getDateState('basic')}
                onChange={updateDateState('basic')}
              />
              <p className="text-sm text-text-secondary">
                Value: {getDateState('basic')?.toString() ?? 'None'}
              </p>
            </div>
          ),
          code: `<DateInput 
  label="Select Date"
  value={date}
  onChange={setDate}
/>`
        },
        {
          title: "With Default Value",
          component: (
            <div className="space-y-4 w-full max-w-xs">
              <DateInput 
                label="Birth Date"
                defaultValue={parseDate('1990-01-01')}
              />
              <p className="text-sm text-text-secondary">
                Uncontrolled with default value
              </p>
            </div>
          ),
          code: `<DateInput 
  label="Birth Date"
  defaultValue={parseDate('1990-01-01')}
/>`
        },
        {
          title: "Required Field",
          component: (
            <div className="space-y-4 w-full max-w-xs">
              <DateInput 
                label="Appointment Date"
                isRequired
                value={getDateState('appointment')}
                onChange={updateDateState('appointment')}
              />
              <p className="text-sm text-text-secondary">
                This field is required
              </p>
            </div>
          ),
          code: `<DateInput 
  label="Appointment Date"
  isRequired
  value={date}
  onChange={setDate}
/>`
        }
      ]
    },
    {
      title: "Variants & Colors",
      description: "Different visual styles and color themes",
      examples: [
        {
          title: "Variants",
          component: (
            <div className="space-y-4 w-full max-w-xs">
              <DateInput 
                label="Flat Variant"
                variant="flat"
              />
              <DateInput 
                label="Bordered Variant"
                variant="bordered"
              />
              <DateInput 
                label="Underlined Variant"
                variant="underlined"
              />
              <DateInput 
                label="Faded Variant"
                variant="faded"
              />
            </div>
          ),
          code: `<DateInput variant="flat" label="Flat" />
<DateInput variant="bordered" label="Bordered" />
<DateInput variant="underlined" label="Underlined" />
<DateInput variant="faded" label="Faded" />`
        },
        {
          title: "Colors",
          component: (
            <div className="space-y-4 w-full max-w-xs">
              <DateInput 
                label="Primary"
                color="primary"
                variant="bordered"
              />
              <DateInput 
                label="Secondary"
                color="secondary"
                variant="bordered"
              />
              <DateInput 
                label="Success"
                color="success"
                variant="bordered"
              />
              <DateInput 
                label="Warning"
                color="warning"
                variant="bordered"
              />
              <DateInput 
                label="Danger"
                color="danger"
                variant="bordered"
              />
            </div>
          ),
          code: `<DateInput color="primary" variant="bordered" />
<DateInput color="secondary" variant="bordered" />
<DateInput color="success" variant="bordered" />
<DateInput color="warning" variant="bordered" />
<DateInput color="danger" variant="bordered" />`
        }
      ]
    },
    {
      title: "Label Placement",
      description: "Different label positioning options",
      examples: [
        {
          title: "Label Positions",
          component: (
            <div className="space-y-6 w-full max-w-xs">
              <DateInput 
                label="Inside Label"
                labelPlacement="inside"
                variant="bordered"
              />
              <DateInput 
                label="Outside Label"
                labelPlacement="outside"
                variant="bordered"
              />
              <DateInput 
                label="Outside Left"
                labelPlacement="outside-left"
                variant="bordered"
              />
            </div>
          ),
          code: `<DateInput labelPlacement="inside" label="Inside" />
<DateInput labelPlacement="outside" label="Outside" />
<DateInput labelPlacement="outside-left" label="Outside Left" />`
        }
      ]
    },
    {
      title: "Date Constraints",
      description: "Min/max dates and validation",
      examples: [
        {
          title: "Date Range",
          component: (
            <div className="space-y-4 w-full max-w-xs">
              <DateInput 
                label="Meeting Date"
                description="Must be within the next 30 days"
                minValue={today(getLocalTimeZone())}
                maxValue={today(getLocalTimeZone()).add({ days: 30 })}
                value={getDateState('meeting')}
                onChange={updateDateState('meeting')}
                variant="bordered"
              />
              <p className="text-sm text-text-secondary">
                Range: Today to {today(getLocalTimeZone()).add({ days: 30 }).toString()}
              </p>
            </div>
          ),
          code: `<DateInput 
  label="Meeting Date"
  minValue={today(getLocalTimeZone())}
  maxValue={today(getLocalTimeZone()).add({ days: 30 })}
  value={date}
  onChange={setDate}
/>`
        },
        {
          title: "Past Dates Only",
          component: (
            <div className="space-y-4 w-full max-w-xs">
              <DateInput 
                label="Birth Date"
                description="Select a date in the past"
                maxValue={today(getLocalTimeZone()).subtract({ days: 1 })}
                value={getDateState('birthday')}
                onChange={updateDateState('birthday')}
                variant="bordered"
                color="secondary"
              />
              <p className="text-sm text-text-secondary">
                Maximum: Yesterday
              </p>
            </div>
          ),
          code: `<DateInput 
  label="Birth Date"
  maxValue={today(getLocalTimeZone()).subtract({ days: 1 })}
  value={date}
  onChange={setDate}
/>`
        }
      ]
    },
    {
      title: "States & Validation",
      description: "Different input states and validation feedback",
      examples: [
        {
          title: "Input States",
          component: (
            <div className="space-y-4 w-full max-w-xs">
              <DateInput 
                label="Disabled Date"
                isDisabled
                defaultValue={today(getLocalTimeZone())}
                variant="bordered"
              />
              <DateInput 
                label="Read Only"
                isReadOnly
                defaultValue={today(getLocalTimeZone())}
                variant="bordered"
                color="secondary"
              />
              <DateInput 
                label="Invalid Date"
                isInvalid
                errorMessage="Please select a valid date"
                variant="bordered"
                color="danger"
              />
            </div>
          ),
          code: `<DateInput isDisabled defaultValue={date} />
<DateInput isReadOnly defaultValue={date} />
<DateInput isInvalid errorMessage="Invalid date" />`
        }
      ]
    },
    {
      title: "Granularity & Time",
      description: "Date inputs with different time precision",
      examples: [
        {
          title: "Date with Time",
          component: (
            <div className="space-y-4 w-full max-w-xs">
              <DateInput 
                label="Date Only"
                granularity="day"
                variant="bordered"
                color="primary"
              />
              <DateInput 
                label="Date & Time"
                granularity="minute"
                variant="bordered"
                color="secondary"
              />
              <DateInput 
                label="Date & Seconds"
                granularity="second"
                variant="bordered"
                color="success"
              />
            </div>
          ),
          code: `<DateInput granularity="day" label="Date Only" />
<DateInput granularity="minute" label="Date & Time" />
<DateInput granularity="second" label="Date & Seconds" />`
        }
      ]
    },
    {
      title: "With Icons & Content",
      description: "Date inputs with additional visual elements",
      examples: [
        {
          title: "Start & End Content",
          component: (
            <div className="space-y-4 w-full max-w-xs">
              <DateInput 
                label="Event Date"
                startContent={<Calendar className="w-4 h-4 text-primary" />}
                variant="bordered"
                color="primary"
              />
              <DateInput 
                label="Deadline"
                startContent={<AlertCircle className="w-4 h-4 text-warning" />}
                endContent={<Clock className="w-4 h-4 text-text-secondary" />}
                granularity="minute"
                variant="bordered"
                color="warning"
              />
              <DateInput 
                label="Completed"
                startContent={<CheckCircle className="w-4 h-4 text-success" />}
                isReadOnly
                defaultValue={today(getLocalTimeZone())}
                variant="bordered"
                color="success"
              />
            </div>
          ),
          code: `<DateInput 
  startContent={<Calendar className="w-4 h-4" />}
  label="Event Date"
/>

<DateInput 
  startContent={<AlertCircle className="w-4 h-4" />}
  endContent={<Clock className="w-4 h-4" />}
  label="Deadline"
/>`
        }
      ]
    },
    {
      title: "Form Example",
      description: "Real-world usage in a form interface",
      examples: [
        {
          title: "Event Form",
          component: (
            <Card className="max-w-md">
              <CardHeader>
                <h3 className="text-lg font-semibold">Schedule Event</h3>
              </CardHeader>
              <CardBody className="space-y-4">
                <DateInput 
                  label="Event Date"
                  description="Select the date for your event"
                  startContent={<CalendarDays className="w-4 h-4 text-primary" />}
                  minValue={today(getLocalTimeZone())}
                  isRequired
                  variant="bordered"
                  color="primary"
                />
                
                <DateInput 
                  label="Registration Deadline"
                  description="Last day to register"
                  startContent={<Clock className="w-4 h-4 text-warning" />}
                  minValue={today(getLocalTimeZone())}
                  variant="bordered"
                  color="warning"
                />
                
                <DateInput 
                  label="Created Date"
                  description="Event creation date"
                  startContent={<CheckCircle className="w-4 h-4 text-success" />}
                  defaultValue={today(getLocalTimeZone())}
                  isReadOnly
                  variant="bordered"
                  color="success"
                />
                
                <div className="flex items-center gap-2 opacity-50">
                  <Globe className="w-4 h-4 text-secondary" />
                  <div className="flex-1">
                    <DateInput 
                      label="International Event"
                      description="Premium feature"
                      isDisabled
                      variant="bordered"
                      color="secondary"
                    />
                  </div>
                </div>
              </CardBody>
            </Card>
          ),
          code: `<DateInput 
  label="Event Date"
  description="Select the date for your event"
  startContent={<CalendarDays className="w-4 h-4" />}
  minValue={today(getLocalTimeZone())}
  isRequired
  variant="bordered"
/>`
        }
      ]
    }
  ];

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-text-primary">DateInput Examples</h1>
          <p className="text-text-secondary">
            Comprehensive showcase of HeroUI DateInput component with internationalization and theme integration
          </p>
        </div>

        {sections.map((section, sectionIndex) => (
          <Card key={sectionIndex}>
            <CardHeader>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold">{section.title}</h2>
                <Badge color="primary" variant="flat" size="sm">Date input</Badge>
              </div>
            </CardHeader>
            <CardBody className="space-y-6">
              <p className="text-text-secondary">{section.description}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {section.examples.map((example, exampleIndex) => (
                  <div key={exampleIndex} className="space-y-3">
                    <h3 className="font-medium text-text-primary">{example.title}</h3>
                    <div className="p-4 bg-content2 rounded-lg min-h-[120px] flex items-center justify-center">
                      {example.component}
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        ))}
        
      </div>
    </PlaygroundPageLayout>
  );
};